import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, Eye, Users, Plus, Trash2, BarChart3,
  ShoppingBag, Star, Check, Send, Clock, Sparkles, Image, Video, Link, Package, Tag,
  Zap, Upload, FileImage, Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { SegmentManager } from './SegmentManager';
import { EmailAutomations } from './EmailAutomations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmailMarketing = ({
  headers,
  fetchData,
  // Stats
  subscriberStats,
  emailStats,
  trackingStats,
  schedulerStatus,
  // Deliverability
  deliverabilityStats,
  deliverabilityDaily,
  deliverabilityAlerts,
  suppressionList,
  // Lists
  emailCampaigns,
  emailSubscribers,
  emailTemplates,
  dailyAnalytics,
  templateAnalytics,
  subscriberGrowth,
  unsubscribeList,
  // Advanced Features
  abandonedCarts,
  abandonedCartStats,
  reviewRequests,
  reviewRequestStats,
  abTests,
  templateSettings
}) => {
  const [templatePreview, setTemplatePreview] = useState(null);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [products, setProducts] = useState([]);
  const [segments, setSegments] = useState([]);
  const [estimatedRecipients, setEstimatedRecipients] = useState(null);
  const [showAiPromptDialog, setShowAiPromptDialog] = useState(false);
  const [aiCustomPrompt, setAiCustomPrompt] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '', 
    subject: '', 
    content: '',
    cta_text: 'Shop Now',
    cta_url: '/products',
    target_list: 'all',
    target_segments: [],
    products: [],
    media: [],
    // Scheduling options
    scheduled_for: '',
    use_optimal_timing: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  useEffect(() => {
    fetchProducts();
    fetchSegments();
  }, []);

  // Estimate recipients when targeting changes
  useEffect(() => {
    if (showCampaignDialog) {
      estimateRecipients();
    }
  }, [campaignForm.target_list, campaignForm.target_segments, showCampaignDialog]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products');
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/emails/segments`, { headers });
      setSegments(res.data);
    } catch (err) {
      console.error('Failed to fetch segments');
    }
  };

  const estimateRecipients = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/emails/campaigns/estimate-recipients`, {
        target_list: campaignForm.target_list,
        target_segments: campaignForm.target_segments
      }, { headers });
      setEstimatedRecipients(res.data.estimated_recipients);
    } catch (err) {
      setEstimatedRecipients(null);
    }
  };

  const toggleSegment = (segmentId) => {
    setCampaignForm(prev => ({
      ...prev,
      target_segments: prev.target_segments.includes(segmentId)
        ? prev.target_segments.filter(id => id !== segmentId)
        : [...prev.target_segments, segmentId]
    }));
  };

  const generateAIContent = async (customPrompt = '') => {
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/email`, {
        campaign_type: 'promotional',
        tone: 'friendly',
        include_cta: true,
        custom_prompt: customPrompt || aiCustomPrompt || undefined
      }, { headers });

      if (res.data.success) {
        const content = res.data.content;
        setCampaignForm(prev => ({
          ...prev,
          subject: content.subject_lines?.[0] || prev.subject,
          content: content.body_html || prev.content,
          cta_text: content.cta_text || prev.cta_text
        }));
        toast.success('AI content generated!');
        setShowAiPromptDialog(false);
        setAiCustomPrompt('');
      }
    } catch (err) {
      toast.error('Failed to generate AI content');
    } finally {
      setAiGenerating(false);
    }
  };

  // Handle image file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'emails');

    try {
      const res = await axios.post(`${API_URL}/api/landing-pages/upload-media`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.url) {
        addMediaToEmail('image', res.data.url);
        toast.success('Image uploaded successfully');
      }
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const addProductToEmail = (product) => {
    if (campaignForm.products.some(p => p.product_id === product.id)) {
      toast.error('Product already added');
      return;
    }
    setCampaignForm(prev => ({
      ...prev,
      products: [...prev.products, {
        product_id: product.id,
        product_name: product.name,
        product_image: product.images?.[0] || product.image_url || '',
        product_price: product.price,
        product_link: `/products/${product.id}`
      }]
    }));
  };

  const removeProductFromEmail = (index) => {
    setCampaignForm(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const addMediaToEmail = (type, url) => {
    setCampaignForm(prev => ({
      ...prev,
      media: [...prev.media, { type, url, alt_text: '', caption: '' }]
    }));
  };

  const removeMediaFromEmail = (index) => {
    setCampaignForm(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/emails/campaigns`, campaignForm, { headers });
      toast.success('Campaign created!');
      setShowCampaignDialog(false);
      setCampaignForm({ 
        name: '', 
        subject: '', 
        content: '',
        cta_text: 'Shop Now',
        cta_url: '/products',
        target_list: 'all',
        products: [],
        media: []
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Send this campaign now?')) return;
    try {
      const res = await axios.post(`${API_URL}/api/emails/campaigns/${campaignId}/send`, {}, { headers });
      toast.success(`Campaign sent to ${res.data.recipients_count} recipients!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send campaign');
    }
  };

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-[#0a0203] mb-6">Email Marketing</h1>
      
      {/* Navigation Tabs */}
      <Tabs defaultValue="campaigns" className="w-full">
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="campaigns" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Mail className="h-4 w-4 mr-2" /> Campaigns & Analytics
          </TabsTrigger>
          <TabsTrigger value="automations" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Zap className="h-4 w-4 mr-2" /> Automations
          </TabsTrigger>
          <TabsTrigger value="segments" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Tag className="h-4 w-4 mr-2" /> Segments
          </TabsTrigger>
        </TabsList>

        {/* Campaigns & Analytics Tab */}
        <TabsContent value="campaigns">
      
      {/* Email Deliverability Monitoring */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#0a0203]">Email Deliverability</h2>
            <p className="text-sm text-gray-500">Monitor your email health score and delivery metrics</p>
          </div>
          {deliverabilityStats && (
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              deliverabilityStats.health_status === 'Good' ? 'bg-green-100 text-green-700' :
              deliverabilityStats.health_status === 'Warning' ? 'bg-yellow-100 text-yellow-700' :
              deliverabilityStats.health_status === 'Critical' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              Health Score: {deliverabilityStats.health_score}/100
            </div>
          )}
        </div>
        <div className="p-6">
          {/* Health Alerts */}
          {deliverabilityAlerts.length > 0 && (
            <div className="mb-6 space-y-3">
              {deliverabilityAlerts.map((alert, idx) => (
                <div key={idx} className={`p-4 rounded-xl flex items-start gap-3 ${
                  alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                  alert.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                  alert.severity === 'success' ? 'bg-green-50 border border-green-200' :
                  'bg-blue-50 border border-blue-200'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    alert.severity === 'critical' ? 'bg-red-500' :
                    alert.severity === 'warning' ? 'bg-yellow-500' :
                    alert.severity === 'success' ? 'bg-green-500' :
                    'bg-blue-500'
                  }`}>
                    {alert.severity === 'critical' && <span className="text-white text-xs">!</span>}
                    {alert.severity === 'warning' && <span className="text-white text-xs">⚠</span>}
                    {alert.severity === 'success' && <Check className="h-3 w-3 text-white" />}
                    {alert.severity === 'info' && <span className="text-white text-xs">i</span>}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      alert.severity === 'critical' ? 'text-red-800' :
                      alert.severity === 'warning' ? 'text-yellow-800' :
                      alert.severity === 'success' ? 'text-green-800' :
                      'text-blue-800'
                    }`}>{alert.message}</p>
                    <p className={`text-sm mt-1 ${
                      alert.severity === 'critical' ? 'text-red-600' :
                      alert.severity === 'warning' ? 'text-yellow-600' :
                      alert.severity === 'success' ? 'text-green-600' :
                      'text-blue-600'
                    }`}>{alert.recommendation}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Delivery Metrics Grid */}
          <div className="grid md:grid-cols-6 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{deliverabilityStats?.total_sent || 0}</p>
              <p className="text-xs text-blue-700">Total Sent</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{deliverabilityStats?.delivery_rate || 0}%</p>
              <p className="text-xs text-green-700">Delivery Rate</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{deliverabilityStats?.bounce_rate || 0}%</p>
              <p className="text-xs text-red-700">Bounce Rate</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{deliverabilityStats?.complaint_rate || 0}%</p>
              <p className="text-xs text-orange-700">Complaints</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{deliverabilityStats?.open_rate || 0}%</p>
              <p className="text-xs text-purple-700">Open Rate</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{deliverabilityStats?.click_rate || 0}%</p>
              <p className="text-xs text-indigo-700">Click Rate</p>
            </div>
          </div>

          {/* Daily Trend Chart */}
          {deliverabilityDaily.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">14-Day Delivery Trend</h3>
              <div className="h-32 flex items-end gap-1">
                {deliverabilityDaily.map((day, idx) => {
                  const sent = day.sent || 0;
                  const delivered = day.delivered || 0;
                  const maxVal = Math.max(...deliverabilityDaily.map(d => d.sent || 1));
                  const height = sent > 0 ? (sent / maxVal * 100) : 5;
                  const deliveryPct = sent > 0 ? (delivered / sent * 100) : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className={`w-full rounded-t transition-all ${
                          deliveryPct >= 95 ? 'bg-green-400' :
                          deliveryPct >= 85 ? 'bg-yellow-400' :
                          deliveryPct > 0 ? 'bg-red-400' : 'bg-gray-200'
                        }`}
                        style={{ height: `${height}%`, minHeight: '4px' }}
                        title={`${day.date}: ${sent} sent, ${delivered} delivered (${deliveryPct.toFixed(0)}%)`}
                      ></div>
                      {idx % 2 === 0 && (
                        <span className="text-[10px] text-gray-400 mt-1">{day.date?.slice(5)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded"></span> ≥95%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-400 rounded"></span> 85-95%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded"></span> &lt;85%</span>
              </div>
            </div>
          )}

          {/* Suppression List */}
          {suppressionList.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Suppressed Emails ({suppressionList.length})</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Reason</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppressionList.slice(0, 10).map((item, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-4 py-2 text-gray-900">{item.email}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.reason === 'bounce' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                          }`}>
                            {item.reason === 'bounce' ? 'Bounced' : 'Complaint'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API_URL}/api/emails/deliverability/suppression-list/${encodeURIComponent(item.email)}`, { headers });
                                toast.success('Removed from suppression list');
                                fetchData();
                              } catch (err) {
                                toast.error('Failed to remove');
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-2">Emails on this list will not receive any future emails.</p>
            </div>
          )}

          {!deliverabilityStats?.total_sent && (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No email data yet</p>
              <p className="text-xs text-gray-400 mt-1">Stats will appear as you send emails. Configure webhooks in Resend for real-time tracking.</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0a0203]">{subscriberStats?.newsletter_subscribers || 0}</p>
              <p className="text-gray-500 text-sm">Newsletter Subscribers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Eye className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0a0203]">{subscriberStats?.video_subscribers || 0}</p>
              <p className="text-gray-500 text-sm">Video Subscribers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0a0203]">{emailStats?.total_sent || 0}</p>
              <p className="text-gray-500 text-sm">Emails Sent</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0a0203]">{trackingStats?.overall?.open_rate?.toFixed(1) || 0}%</p>
              <p className="text-gray-500 text-sm">Delivery Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduler Status */}
      {schedulerStatus && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#0a0203]">Automated Email Scheduler</h2>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-3 h-3 rounded-full ${schedulerStatus.running ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="font-medium">{schedulerStatus.running ? 'Running' : 'Stopped'}</span>
              <span className="text-sm text-gray-500">Interval: {schedulerStatus.interval_minutes} min</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Last run: {schedulerStatus.last_run ? new Date(schedulerStatus.last_run).toLocaleString() : 'Never'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Next run: {schedulerStatus.next_run ? new Date(schedulerStatus.next_run).toLocaleString() : 'Unknown'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Campaigns */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#0a0203]">Email Campaigns</h2>
            <p className="text-sm text-gray-500">Create and send marketing emails to your subscribers</p>
          </div>
          <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#003456] hover:bg-[#001d30]" data-testid="new-campaign-btn">
                <Plus className="h-5 w-5 mr-2" /> New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create Email Campaign</DialogTitle></DialogHeader>
              
              {/* AI Content Generator with Custom Prompt */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    <span className="font-medium text-purple-800">AI Content Generator</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowAiPromptDialog(true)}
                      variant="outline"
                      className="border-purple-300 text-purple-700"
                      size="sm"
                      data-testid="ai-custom-prompt-btn"
                    >
                      Custom Prompt
                    </Button>
                    <Button
                      type="button"
                      onClick={() => generateAIContent()}
                      disabled={aiGenerating}
                      className="bg-purple-600 hover:bg-purple-700"
                      size="sm"
                      data-testid="ai-quick-generate-btn"
                    >
                      {aiGenerating ? 'Generating...' : 'Quick Generate'}
                    </Button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleCreateCampaign}>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column - Editor */}
                  <div className="space-y-4">
                    <Input 
                      placeholder="Campaign Name" 
                      value={campaignForm.name}
                      onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                      required
                      data-testid="campaign-name-input"
                    />
                    <Input 
                      placeholder="Email Subject" 
                      value={campaignForm.subject}
                      onChange={(e) => setCampaignForm({...campaignForm, subject: e.target.value})}
                      required
                      data-testid="campaign-subject-input"
                    />
                    
                    {/* Email Content */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Email Content (HTML)</label>
                      <textarea 
                        className="w-full p-3 border rounded-lg min-h-[200px] font-mono text-sm"
                        placeholder="<p>Your email content here...</p>"
                        value={campaignForm.content}
                        onChange={(e) => setCampaignForm({...campaignForm, content: e.target.value})}
                        required
                        data-testid="campaign-content-textarea"
                      />
                    </div>

                    {/* CTA Settings */}
                    <div className="grid grid-cols-2 gap-4">
                      <Input 
                        placeholder="CTA Button Text" 
                        value={campaignForm.cta_text}
                        onChange={(e) => setCampaignForm({...campaignForm, cta_text: e.target.value})}
                      />
                      <Input 
                        placeholder="CTA Link URL" 
                        value={campaignForm.cta_url}
                        onChange={(e) => setCampaignForm({...campaignForm, cta_url: e.target.value})}
                      />
                    </div>

                    {/* Target Audience */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="font-medium text-gray-700">Target Audience</label>
                        {estimatedRecipients !== null && (
                          <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            ~{estimatedRecipients} recipients
                          </span>
                        )}
                      </div>
                      <select 
                        className="w-full p-3 border rounded-lg"
                        value={campaignForm.target_list}
                        onChange={(e) => setCampaignForm({...campaignForm, target_list: e.target.value})}
                        data-testid="campaign-target-list"
                      >
                        <option value="all">All Subscribers</option>
                        <option value="newsletter">Newsletter Only</option>
                        <option value="video">Video Gallery Only</option>
                      </select>

                      {/* Segment Selection */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Target Segments (optional)</span>
                        </div>
                        <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                          {segments.map(segment => (
                            <button
                              key={segment.id}
                              type="button"
                              onClick={() => toggleSegment(segment.id)}
                              className={`px-3 py-1 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                                campaignForm.target_segments.includes(segment.id)
                                  ? 'bg-[#003456] text-white border-[#003456]'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#003456]'
                              }`}
                            >
                              {campaignForm.target_segments.includes(segment.id) && (
                                <Check className="h-3 w-3" />
                              )}
                              {segment.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Featured Products */}
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">Featured Products</span>
                      </div>
                      
                      {campaignForm.products.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {campaignForm.products.map((p, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                              <span className="text-sm">{p.product_name}</span>
                              <button type="button" onClick={() => removeProductFromEmail(idx)} className="text-red-500 hover:text-red-700">
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <select 
                        className="w-full p-2 border rounded-lg text-sm"
                        onChange={(e) => {
                          const product = products.find(p => p.id === e.target.value);
                          if (product) addProductToEmail(product);
                          e.target.value = '';
                        }}
                        defaultValue=""
                      >
                        <option value="">+ Add product to email</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} - ${p.price}</option>
                        ))}
                      </select>
                    </div>

                    {/* Media Section - Enhanced with File Upload */}
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Image className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">Media Embeds</span>
                      </div>

                      {campaignForm.media.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {campaignForm.media.map((m, idx) => (
                            <div key={idx} className="relative group border rounded-lg overflow-hidden">
                              {m.type === 'image' ? (
                                <img src={m.url} alt="" className="w-full h-20 object-cover" />
                              ) : (
                                <div className="w-full h-20 bg-gray-100 flex items-center justify-center">
                                  <Video className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removeMediaFromEmail(idx)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-2">
                        {/* File Upload */}
                        <div>
                          <input
                            type="file"
                            ref={imageInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                            id="campaign-image-upload"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full"
                            disabled={uploadingImage}
                            onClick={() => imageInputRef.current?.click()}
                            data-testid="upload-image-btn"
                          >
                            {uploadingImage ? (
                              'Uploading...'
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-1" /> Upload
                              </>
                            )}
                          </Button>
                        </div>
                        {/* URL Input */}
                        <div>
                          <Input 
                            placeholder="Image URL"
                            id="image-url-input"
                            className="text-xs h-9"
                          />
                        </div>
                        <div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              const url = document.getElementById('image-url-input').value;
                              if (url) {
                                addMediaToEmail('image', url);
                                document.getElementById('image-url-input').value = '';
                              }
                            }}
                          >
                            <Image className="h-4 w-4 mr-1" /> Add
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <Input 
                          placeholder="Video/YouTube URL"
                          id="video-url-input"
                          className="text-xs"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const url = document.getElementById('video-url-input').value;
                            if (url) {
                              addMediaToEmail('video', url);
                              document.getElementById('video-url-input').value = '';
                            }
                          }}
                        >
                          <Video className="h-4 w-4 mr-1" /> Add Video
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Live Preview */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">Live Preview</label>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Updates in real-time</span>
                    </div>
                    <div className="border rounded-xl overflow-hidden shadow-sm sticky top-4">
                      {/* Email Header Preview */}
                      <div className="bg-gray-100 p-3 border-b">
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                          <Mail className="h-3 w-3" />
                          <span>From: HomeLyph &lt;hello@homelyph.com&gt;</span>
                        </div>
                        <p className="text-xs text-gray-500">Subject</p>
                        <p className="text-sm font-medium text-gray-900">{campaignForm.subject || 'No subject'}</p>
                      </div>
                      {/* Email Body Preview */}
                      <div className="bg-white p-4 max-h-[500px] overflow-y-auto">
                        {campaignForm.content ? (
                          <div 
                            dangerouslySetInnerHTML={{ __html: campaignForm.content }} 
                            className="prose prose-sm max-w-none"
                          />
                        ) : (
                          <div className="text-center py-12 text-gray-400">
                            <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                            <p>Start typing to see preview</p>
                          </div>
                        )}
                        
                        {/* Media Preview */}
                        {campaignForm.media.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {campaignForm.media.filter(m => m.type === 'image').map((m, idx) => (
                              <img key={idx} src={m.url} alt="" className="max-w-full rounded-lg" />
                            ))}
                          </div>
                        )}

                        {/* Products Preview */}
                        {campaignForm.products.length > 0 && (
                          <div className="mt-4 border-t pt-4">
                            <h4 className="text-sm font-semibold mb-2">Featured Products</h4>
                            <div className="space-y-2">
                              {campaignForm.products.map((p, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                  {p.product_image && (
                                    <img src={p.product_image} alt="" className="w-12 h-12 rounded object-cover" />
                                  )}
                                  <div>
                                    <p className="text-sm font-medium">{p.product_name}</p>
                                    <p className="text-xs text-green-600">${p.product_price}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* CTA Preview */}
                        {campaignForm.cta_text && (
                          <div className="mt-6 text-center">
                            <a 
                              href={campaignForm.cta_url || '#'} 
                              className="inline-block bg-[#003456] text-white px-6 py-3 rounded-lg font-medium"
                              onClick={(e) => e.preventDefault()}
                            >
                              {campaignForm.cta_text}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scheduling Options */}
                <div className="mt-6 border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4 text-gray-600" />
                    <span className="font-medium text-gray-700">Schedule Campaign</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Send Date & Time</label>
                      <Input
                        type="datetime-local"
                        value={campaignForm.scheduled_for}
                        onChange={(e) => setCampaignForm({...campaignForm, scheduled_for: e.target.value})}
                        className="w-full"
                        data-testid="schedule-datetime"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to send immediately when you click create</p>
                    </div>
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={campaignForm.use_optimal_timing}
                          onChange={(e) => setCampaignForm({...campaignForm, use_optimal_timing: e.target.checked})}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm text-gray-700">Use optimal send time</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6">
                        AI will pick the best time based on subscriber engagement patterns
                      </p>
                      
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Your Timezone</label>
                        <select
                          value={campaignForm.timezone}
                          onChange={(e) => setCampaignForm({...campaignForm, timezone: e.target.value})}
                          className="w-full p-2 border rounded-lg text-sm"
                        >
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <Button type="submit" className="w-full bg-[#003456]" data-testid="create-campaign-btn">
                    {campaignForm.scheduled_for ? 'Schedule Campaign' : 'Create Campaign'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* AI Custom Prompt Dialog */}
          <Dialog open={showAiPromptDialog} onOpenChange={setShowAiPromptDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  AI Content Generator
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-700">
                    <strong>Tips:</strong> Be specific about tone, target audience, promotions, discount codes, or any special messaging to include.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Custom Context/Instructions</label>
                  <textarea
                    value={aiCustomPrompt}
                    onChange={(e) => setAiCustomPrompt(e.target.value)}
                    placeholder="Example: Create a promotional email for our Spring Sale. Target families and emphasize savings. Include a 15% discount code SPRING15 and highlight our fast shipping options."
                    className="w-full mt-1 p-3 border rounded-lg h-32 resize-none"
                    data-testid="ai-prompt-textarea"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAiPromptDialog(false)}>Cancel</Button>
                  <Button
                    onClick={() => generateAIContent(aiCustomPrompt)}
                    disabled={aiGenerating}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="ai-generate-btn"
                  >
                    {aiGenerating ? 'Generating...' : 'Generate Content'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="overflow-x-auto">
          {emailCampaigns.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sent</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {emailCampaigns.map(campaign => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-[#0a0203]">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.subject}</div>
                    </td>
                    <td className="px-6 py-4 capitalize text-gray-600">{campaign.target}</td>
                    <td className="px-6 py-4 text-center font-medium">{campaign.recipients_count || '-'}</td>
                    <td className="px-6 py-4 text-center">{campaign.sent_at ? new Date(campaign.sent_at).toLocaleDateString() : '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        campaign.status === 'sent' ? 'bg-green-100 text-green-600' :
                        campaign.status === 'sending' ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {campaign.status === 'draft' && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleSendCampaign(campaign.id)}
                        >
                          <Send className="h-4 w-4 mr-1" /> Send
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No campaigns yet</p>
              <p className="text-sm text-gray-400 mt-2">Create your first email campaign</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Templates Preview */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-[#0a0203]">Email Templates</h2>
          <p className="text-sm text-gray-500">Preview your automated email templates</p>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {emailTemplates.map(t => (
              <Button
                key={t.id}
                variant={templatePreview?.id === t.id ? "default" : "outline"}
                size="sm"
                onClick={() => setTemplatePreview(t)}
                className={templatePreview?.id === t.id ? "bg-[#003456]" : ""}
              >
                {t.name}
              </Button>
            ))}
          </div>
          {templatePreview && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="text-sm text-gray-600 mb-2">Subject: <strong>{templatePreview.subject}</strong></div>
              <div 
                className="bg-white border rounded p-4 max-h-96 overflow-auto"
                dangerouslySetInnerHTML={{ __html: templatePreview.html_preview || templatePreview.description }}
              />
            </div>
          )}
          {!templatePreview && (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Select a template above to preview</p>
            </div>
          )}
        </div>
      </div>

      {/* Abandoned Cart Analytics */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-[#0a0203]">Abandoned Cart Recovery</h2>
          <p className="text-sm text-gray-500">Track and recover abandoned shopping carts</p>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{abandonedCartStats?.total_carts || 0}</p>
              <p className="text-sm text-orange-700">Total Carts</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{abandonedCartStats?.recovered || 0}</p>
              <p className="text-sm text-green-700">Recovered</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{abandonedCartStats?.pending || 0}</p>
              <p className="text-sm text-yellow-700">Pending</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">${(abandonedCartStats?.potential_revenue || 0).toLocaleString()}</p>
              <p className="text-sm text-blue-700">Potential Revenue</p>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">Recovery Rate</span>
              <span className="text-sm font-bold text-green-600">{abandonedCartStats?.recovery_rate || 0}%</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: `${abandonedCartStats?.recovery_rate || 0}%` }}
              ></div>
            </div>
          </div>

          {abandonedCarts.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Abandoned Carts</h3>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Items</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Emails Sent</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {abandonedCarts.slice(0, 10).map((cart, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-sm text-gray-900">{cart.email}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{cart.items?.length || 0}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">${(cart.total || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{cart.emails_sent || 0}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          cart.recovered ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                        }`}>
                          {cart.recovered ? 'Recovered' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {cart.created_at ? new Date(cart.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <ShoppingBag className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No abandoned carts yet</p>
              <p className="text-xs text-gray-400 mt-1">Carts are tracked when customers enter their email on the cart page</p>
            </div>
          )}
        </div>
      </div>

      {/* Post-Purchase Review Requests */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-[#0a0203]">Post-Purchase Review Requests</h2>
          <p className="text-sm text-gray-500">Automated emails asking customers to review their purchases</p>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#f0f4f8] rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-[#003456]">{reviewRequestStats?.total_requests || 0}</p>
              <p className="text-sm text-gray-600">Total Requests</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{reviewRequestStats?.sent || 0}</p>
              <p className="text-sm text-green-700">Sent Successfully</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{reviewRequestStats?.pending || 0}</p>
              <p className="text-sm text-yellow-700">Pending</p>
            </div>
          </div>

          {reviewRequests.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Review Requests</h3>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Customer</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Order ID</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Scheduled</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewRequests.slice(0, 10).map((req, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-sm text-gray-900">{req.customer_email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{req.order_id?.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          req.sent && req.success ? 'bg-green-100 text-green-600' :
                          req.sent && !req.success ? 'bg-red-100 text-red-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {req.sent && req.success ? 'Sent' : req.sent && !req.success ? 'Failed' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {req.scheduled_for ? new Date(req.scheduled_for).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Star className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No review requests yet</p>
              <p className="text-xs text-gray-400 mt-1">Review requests are automatically scheduled after order completion</p>
            </div>
          )}
        </div>
      </div>

      {/* A/B Testing */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#0a0203]">A/B Testing</h2>
            <p className="text-sm text-gray-500">Test different email subject lines to optimize open rates</p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#003456] hover:bg-[#001d30]">
                <Plus className="h-5 w-5 mr-2" /> New A/B Test
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create A/B Test</DialogTitle></DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                try {
                  await axios.post(`${API_URL}/api/emails/ab-tests`, {
                    name: formData.get('name'),
                    template_id: formData.get('template_id'),
                    variants: [
                      { subject: formData.get('variant_a'), weight: 50 },
                      { subject: formData.get('variant_b'), weight: 50 }
                    ]
                  }, { headers });
                  toast.success('A/B test created!');
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to create test');
                }
              }} className="space-y-4">
                <Input name="name" placeholder="Test Name (e.g., Welcome Email Subject Test)" required />
                <select name="template_id" className="w-full p-3 border rounded-lg" required>
                  <option value="">Select Template</option>
                  {emailTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variant A (50%)</label>
                  <Input name="variant_a" placeholder="Subject line A" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variant B (50%)</label>
                  <Input name="variant_b" placeholder="Subject line B" required />
                </div>
                <Button type="submit" className="w-full bg-[#003456]">Create A/B Test</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="p-6">
          {abTests.length > 0 ? (
            <div className="space-y-4">
              {abTests.map(test => (
                <div key={test.id} className="border rounded-xl p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-[#0a0203]">{test.name}</h3>
                      <p className="text-sm text-gray-500">Template: {test.template_id}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      test.status === 'active' ? 'bg-green-100 text-green-600' :
                      test.status === 'completed' ? 'bg-blue-100 text-blue-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {test.status}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    {test.variants?.map((variant, vIdx) => {
                      const openRate = variant.sent > 0 ? ((variant.opened / variant.sent) * 100).toFixed(1) : 0;
                      const isWinner = test.winner?.variant_id === variant.id;
                      return (
                        <div key={vIdx} className={`bg-gray-50 rounded-lg p-3 ${isWinner ? 'ring-2 ring-green-500' : ''}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700 truncate flex-1 mr-2">
                              {variant.subject}
                              {isWinner && <span className="ml-2 text-green-600">Winner</span>}
                            </span>
                            <span className="text-xs text-gray-500">{variant.weight}% traffic</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span>Sent: {variant.sent || 0}</span>
                            <span>Opened: {variant.opened || 0}</span>
                            <span className="font-semibold text-green-600">Open Rate: {openRate}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {test.status === 'active' && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API_URL}/api/emails/ab-tests/${test.id}/declare-winner`, {}, { headers });
                            if (res.data.winner) {
                              toast.success(`Winner: "${res.data.winner}" with ${res.data.open_rate}% open rate`);
                            } else {
                              toast.info(res.data.message);
                            }
                            fetchData();
                          } catch (err) {
                            toast.error('Failed to declare winner');
                          }
                        }}
                      >
                        Declare Winner
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={async () => {
                          try {
                            await axios.put(`${API_URL}/api/emails/ab-tests/${test.id}/status?status=paused`, {}, { headers });
                            toast.success('Test paused');
                            fetchData();
                          } catch (err) {
                            toast.error('Failed to pause test');
                          }
                        }}
                      >
                        Pause Test
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No A/B tests yet</p>
              <p className="text-xs text-gray-400 mt-1">Create a test to optimize your email subject lines</p>
            </div>
          )}
        </div>
      </div>

      {/* Template Settings */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-[#0a0203]">Template Settings</h2>
          <p className="text-sm text-gray-500">Customize email template content and settings</p>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-4">
            {['abandoned_cart', 'review_request', 'newsletter_welcome'].map(templateId => {
              const settings = templateSettings[templateId] || {};
              const templateNames = {
                'abandoned_cart': 'Abandoned Cart Email',
                'review_request': 'Review Request Email',
                'newsletter_welcome': 'Newsletter Welcome'
              };
              return (
                <div key={templateId} className="border rounded-xl p-4">
                  <h3 className="font-semibold text-[#0a0203] mb-3">{templateNames[templateId]}</h3>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    try {
                      await axios.put(`${API_URL}/api/emails/templates/settings/${templateId}`, {
                        header_text: formData.get('header_text'),
                        cta_text: formData.get('cta_text'),
                        cta_url: formData.get('cta_url')
                      }, { headers });
                      toast.success('Template settings saved!');
                      fetchData();
                    } catch (err) {
                      toast.error('Failed to save settings');
                    }
                  }} className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Header Text</label>
                      <Input 
                        name="header_text" 
                        defaultValue={settings.header_text || ''} 
                        placeholder="Email header"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">CTA Button Text</label>
                      <Input 
                        name="cta_text" 
                        defaultValue={settings.cta_text || ''} 
                        placeholder="Button text"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">CTA URL</label>
                      <Input 
                        name="cta_url" 
                        defaultValue={settings.cta_url || ''} 
                        placeholder="https://..."
                        className="text-sm"
                      />
                    </div>
                    <Button type="submit" size="sm" className="w-full bg-[#003456]">Save</Button>
                  </form>
                </div>
              );
            })}
          </div>
        </div>
      </div>
        </TabsContent>

        {/* Automations Tab */}
        <TabsContent value="automations">
          <EmailAutomations headers={headers} />
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments">
          <SegmentManager headers={headers} fetchData={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailMarketing;
